import React, { lazy, Suspense, createContext, useEffect } from "react";
import { createRoot } from "react-dom/client";

import { ConfigProvider, theme as antdTheme } from "antd";
import { Provider } from "react-redux";
import { BrowserRouter } from "react-router-dom";

import { useLocalStorage } from "./hooks/useLocalStorage";
import { store } from "./store/index";

export type ThemeMode = "light" | "dark";

export const AppContext = createContext<{
    lang: string;
    setLang: (_value: string) => void;
    theme: ThemeMode;
    setTheme: (_value: ThemeMode) => void;
} | null>(null);

// Keep in sync with the html[data-theme="dark"] palette in styles/colors.less.
const THEME_META_COLOR: Record<ThemeMode, string> = { light: "#ffffff", dark: "#141414" };

const container = document.getElementById("root")!;
const root = createRoot(container);
const App = lazy(() => import("./App"));

const RootApp = () => {
    const [lang, setLang] = useLocalStorage<string>("dashboard_language", "en");
    const [theme, setTheme] = useLocalStorage<ThemeMode>("dashboard_theme", "light");

    useEffect(() => {
        document.documentElement.lang = lang;
        document.documentElement.dir = lang === "he" ? "rtl" : "ltr";
    }, [lang]);

    useEffect(() => {
        document.documentElement.dataset.theme = theme;
        document.querySelector("meta[name=\"theme-color\"]")?.setAttribute("content", THEME_META_COLOR[theme]);
    }, [theme]);

    return (
        <AppContext.Provider value={{ lang, setLang, theme, setTheme }}>
            <ConfigProvider
                theme={{
                    algorithm: theme === "dark" ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
                    cssVar: true,
                }}
            >
                <Provider store={store}>
                    {/* Opt in to the v7 behaviours early: no relative links live under our splat routes. */}
                    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                        <App />
                    </BrowserRouter>
                </Provider>
            </ConfigProvider>
        </AppContext.Provider>
    );
};

root.render(
    <React.StrictMode>
        <Suspense fallback="...loading">
            <RootApp />
        </Suspense>
    </React.StrictMode>
);
